import {AssetReportDocument} from 'common'
import AlgoliaClient, {
  ASSET_REPORTS_ADMIN_PANEL_ALGOLIA_INDEX
} from 'components/AlgoliaSearch'
import {useAppContext, useFirebase} from 'components/Firebase/hooks'
import InputField from 'components/InputField'
import Pagination from 'components/Pagination'
import {TABLE_PAGINATION_PAGE_SIZE} from 'components/constants'
import {searchWithAlgolia} from 'components/utils'
import debounce from 'lodash/debounce'
import React, {useEffect, useRef, useState} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'
import Layout from '../Layout'
import {Loader} from '../Loader'
import NavBar from '../Navbar'
import withAuthorization from '../Session/withAuthorization'
import Table from '../TableComponent'
import styles from './EquipmentReportTable.module.scss'

const EquipmentReportTable = () => {
  const {state} = useLocation()
  const navigate = useNavigate()
  const pageFromLocation = state?.page
  const [accountList, setAccountList] = useState<Array<string>>([])
  const [currentPage, setCurrentPage] = useState<number>(pageFromLocation || 1)
  const [pageCount, setPageCount] = useState<number>(0)
  const [dir, setDir] = useState<{key: string; dir: string}>({
    key: '',
    dir: 'asc'
  })
  const [loader, setLoader] = useState(true)
  const {getUserAccounts} = useFirebase()
  const [assetReportsData, setAssetReportsData] = useState<
    Array<AssetReportDocument>
  >([])
  const {
    appState: {isMSAAdmin, isSuperAdmin, isSuperManager}
  } = useAppContext()

  const getMSAAccountsList = async () => {
    const userAccounts = await getUserAccounts()
    const accountIDLength = userAccounts.length
    if (accountIDLength) {
      !accountList.length && setAccountList([...userAccounts])
    }
  }

  useEffect(() => {
    if (isMSAAdmin) {
      getMSAAccountsList()
    }
  }, [])

  useEffect(() => {
    searchIndex
      .setSettings({
        customRanking: ['desc(lastmodified)']
      })
      .then(() => {
        AlgoliaClient.clearCache()
        if (isMSAAdmin && accountList.length) {
          searchWithAlgolia<AssetReportDocument>({
            searchQuery: '*',
            searchIndex,
            setLoaderState: setLoader,
            setData: setAssetReportsData,
            setPageCount,
            accountList,
            currentPage,
            sortData: false
          }).then(() => setLoader(false))
        }
        if (isSuperAdmin || isSuperManager) {
          searchWithAlgolia<AssetReportDocument>({
            searchQuery: '*',
            searchIndex,
            setLoaderState: setLoader,
            setData: setAssetReportsData,
            setPageCount,
            accountList,
            currentPage,
            sortData: false
          }).then(() => setLoader(false))
        }
      })
  }, [accountList?.length])

  const getRowData = (rowData: Array<AssetReportDocument>) => {
    return (rowData || []).map((singleRowData, index: number) => {
      const {accountName, contactName, contactId} = singleRowData

      return (
        <tr
          key={`equipment-report-row-data-${index}`}
          onClick={() => {
            navigate('/reports/single-report-detail', {
              state: {
                data: singleRowData,
                currentPage
              },
              preventScrollReset: true
            })
          }}
        >
          <td>{accountName || '-'}</td>
          <td>{contactName || '-'}</td>
          <td>{contactId || '-'}</td>
        </tr>
      )
    })
  }

  const scrollRef: React.RefObject<HTMLDivElement> = React.createRef()

  const scrollToRef = () =>
    scrollRef?.current?.scrollIntoView({behavior: 'smooth'})

  const sortBy = async (keys: Array<string>) => {
    await AlgoliaClient.clearCache()
    setLoader(true)
    let newDir = 'asc'
    if (dir.key === keys[0]) {
      newDir = dir.dir === 'asc' ? 'desc' : 'asc'
    }
    setDir({dir: newDir, key: keys[0]})
    await searchIndex.setSettings({
      customRanking: keys.map((key) => newDir + '(' + key + ')')
    })
    setCurrentPage(1)
    setTimeout(() => {
      debouncedSearch?.current({
        searchQuery: searchValue,
        searchIndex,
        setLoaderState: setLoader,
        setData: setAssetReportsData,
        setPageCount,
        accountList,
        currentPage: 1,
        sortData: false
      })
    }, 1000)
  }
  const showPagination = pageCount > TABLE_PAGINATION_PAGE_SIZE

  const onPageChange = (page: number) => {
    setLoader(true)
    setCurrentPage(page)
    debouncedSearch?.current({
      searchQuery: searchValue,
      searchIndex,
      setLoaderState: setLoader,
      setData: setAssetReportsData,
      setPageCount,
      currentPage: page,
      sortData: false
    })
    setLoader(false)
    scrollToRef()
  }

  const tableTitles = [
    {title: 'Account Name', sortBy: () => sortBy(['accountName'])},
    {title: 'Contact Name', sortBy: () => sortBy(['contactName'])},
    {title: 'Contact ID', sortBy: () => sortBy(['contactId'])}
  ]

  const [searchValue, setSearchValue] = useState('')
  const searchIndex = AlgoliaClient.initIndex(
    ASSET_REPORTS_ADMIN_PANEL_ALGOLIA_INDEX
  )
  const debouncedSearch = useRef(
    debounce(searchWithAlgolia<AssetReportDocument>, 800)
  )

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isMSAAdmin && !accountList?.length) return
    const value = event?.target?.value
    setSearchValue(value)
    if (value) {
      debouncedSearch?.current({
        searchQuery: value,
        searchIndex,
        setLoaderState: setLoader,
        setData: setAssetReportsData,
        setPageCount,
        accountList,
        sortData: false
      })
    } else {
      debouncedSearch?.current({
        searchQuery: '*',
        searchIndex,
        setLoaderState: setLoader,
        setData: setAssetReportsData,
        setPageCount,
        currentPage,
        accountList,
        sortData: false
      })
    }
  }
  return (
    <div className={styles.withLayout}>
      <Layout />
      <div className={styles.container}>
        <NavBar title="Equipment Reports" />
        <div className={styles.button_filters}>
          <InputField
            name="searchField"
            type="text"
            value={searchValue}
            onChange={handleSearch}
            customClass={styles.inputField}
            placeholder="Search for Filters"
            isSearch={true}
          />
        </div>
        {loader ? (
          <Loader />
        ) : (
          <React.Fragment>
            <Table
              customClass={styles.threedetails}
              titles={tableTitles}
              rowData={getRowData(assetReportsData)}
            />
            {showPagination && (
              <div className="pagination__wrapper">
                <Pagination
                  currentPage={currentPage}
                  totalCount={pageCount}
                  pageSize={TABLE_PAGINATION_PAGE_SIZE}
                  onPageChange={onPageChange}
                />
              </div>
            )}
          </React.Fragment>
        )}
      </div>
    </div>
  )
}

export default withAuthorization(EquipmentReportTable)
